import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaSearch, FaTimes, FaFilePdf, FaImage, FaCheckCircle, FaUser, FaPhone, FaBuilding } from 'react-icons/fa';
import pdfIcon from '../../../images/pdf-icon.svg';
import eyeIcon from '../../../images/eye.svg';
import Select from 'react-select';
import StyledTappable from './StyledTappable';
import LoadingComponent from './LoadingComponent';

import {
  uploadPrescriptionForMed,
  removeFileFromPrescriptionMeds,
  submitDigitsationRequestMeds,
  getUserInfoByPhone,
  uploadPrescriptionForMedDashboard,
} from './Services.js';

/* ── constants ── */
const BRAND   = '#6C5DD3';
const BORDER  = '#e0daf8';
const SURFACE = '#ffffff';
const TEXT     = '#0f172a';
const MUTED    = '#64748b';

/* ── tiny helpers ── */
const card = {
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '10px',
  boxShadow: '0 2px 8px rgba(108,93,211,0.06)',
  padding: '20px',
  marginBottom: '16px',
};

const stepLabel = (n, label) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
    <div style={{
      width: '22px', height: '22px', borderRadius: '50%',
      background: BRAND, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: '700', flexShrink: 0,
    }}>{n}</div>
    <span style={{ fontSize: '12px', fontWeight: '700', color: TEXT, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </span>
  </div>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? BRAND : BORDER,
    boxShadow: state.isFocused ? `0 0 0 2px rgba(108,93,211,0.15)` : 'none',
    borderRadius: '7px',
    fontSize: '13px',
    minHeight: '38px',
    '&:hover': { borderColor: BRAND },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected ? BRAND : state.isFocused ? '#f5f3ff' : '#fff',
    color: state.isSelected ? '#fff' : TEXT,
  }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '13px' }),
};

/* ── styled ── */
const CloseBtn = styled(StyledTappable)`
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ef4444;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ── component ── */
function UploadFile({ onClose }) {
  const [pharmacyPrescriptionId, setPharmacyPrescriptionId] = useState('');
  const [loading, setLoading]               = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [uploadedFiles, setUploadedFiles]   = useState([]);
  const [phone, setPhone]                   = useState('');
  const [sponsor, setSponsor]               = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [user, setUser]                     = useState({});
  const [patients, setPatients]             = useState([]);
  const [patient, setPatient]               = useState({});
  const [addresses, setAddresses]           = useState([]);
  const [address, setAddress]               = useState({});
  const [searchLoading, setSearchLoading]   = useState(false);
  const [searchError, setSearchError]       = useState('');

  const handlePhoneChange = (e) => setPhone(e.target.value);

  const searchUserDataByPhone = async () => {
    if (!phone) return;
    setSearchLoading(true);
    setSearchError('');
    try {
      const userData = await getUserInfoByPhone(phone);
      setUser(userData.user);
      setPatients(userData.patients.map((p) => ({ ...p, value: p.patientId, label: p.name })));
      setAddresses(userData.addresses.map((a) => ({ ...a, value: a.addressId, label: a })));
      setSponsor(userData.sponsor);
    } catch (err) {
      setSearchError('User not found. Please check the phone number.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAttachment = (type) => {
    let inputElem = document.getElementById('fileAttachment');
    if (type === 'photo') inputElem = document.getElementById('photoAttachment');

    const file = inputElem.files[0];
    if (!file) return;

    const data = new FormData();
    data.append('file', file);
    if (pharmacyPrescriptionId) data.append('pharmacyPrescriptionId', pharmacyPrescriptionId);

    setUploading(true);

    const onUploadProgress = (progressEvent) => {
      if (progressEvent.lengthComputable) {
        const { loaded, total } = progressEvent;
        setUploadProgress((loaded / total) * 100);
      } else {
        setUploadProgress(100);
      }
    };

    uploadPrescriptionForMed(user.userId, data, onUploadProgress)
      .then((res) => {
        setUploading(false);
        setPharmacyPrescriptionId(res.pharmacyPrescriptionId);
        fetchPrescription();
      })
      .catch((err) => {
        console.log(err);
        setUploading(false);
      });
  };

  const handleSubmit = async () => {
    setLoading(true);
    await submitDigitsationRequestMeds(
      pharmacyPrescriptionId,
      address.addressId,
      patient.patientId,
      user.userId,
    )
      .then((res) => {
        if (res.message === 'success') {
          onClose();
        } else {
          alert('Error in uploading');
        }
      })
      .catch((err) => console.log(err));
    setLoading(false);
  };

  const fetchPrescription = () => {
    uploadPrescriptionForMedDashboard(user.userId)
      .then((res) => {
        const { previewFiles, pharmacyPrescriptionId: pid } = res;
        setUploadedFiles(previewFiles);
        setPharmacyPrescriptionId(pid);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const removeFile = (fileId) => {
    removeFileFromPrescriptionMeds(fileId, pharmacyPrescriptionId)
      .then(() => fetchPrescription())
      .catch((err) => console.log(err));
  };

  useEffect(() => {}, []);

  const formatAddressOptionLabel = (addr) => (
    <div style={{ padding: '4px 0', fontSize: '12px', lineHeight: '1.6' }}>
      <strong>{addr.address}</strong>
      {addr.city && <span style={{ color: MUTED }}> · {addr.city}</span>}
      {addr.pincode && <span style={{ color: MUTED }}> · {addr.pincode}</span>}
      {addr.latestDigitisationRequestId && (
        <span style={{ color: BRAND, fontWeight: '600' }}> · ID: {addr.latestDigitisationRequestId}</span>
      )}
    </div>
  );

  const renderUploadedFile = (ele, idx) => {
    const isPdf = ele.fileType === 'pdf';
    return (
      <div key={ele.fileId} style={{
        position: 'relative',
        width: '80px', height: '96px',
        borderRadius: '10px',
        border: `1.5px solid ${BORDER}`,
        background: isPdf ? '#fef3f2' : '#f5f3ff',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', overflow: 'visible',
        flexShrink: 0,
      }}
        onClick={() => document.querySelector(`#image-anchor-${ele.fileId}`).click()}
      >
        {isPdf ? (
          <img src={pdfIcon} alt="pdf" style={{ width: '36px', height: '48px' }} />
        ) : (
          <div style={{
            width: '100%', height: '100%', borderRadius: '10px',
            backgroundImage: `url(${ele.fileUrl})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
          }} />
        )}
        <a href={ele.fileUrl} target="_blank" rel="noopener noreferrer"
          id={`image-anchor-${ele.fileId}`} style={{ display: 'none' }}>
          {' '}
        </a>
        <CloseBtn onTap={() => removeFile(ele.fileId)}>
          <FaTimes style={{ color: '#fff', fontSize: '9px' }} />
        </CloseBtn>
        {!isPdf && (
          <img src={eyeIcon} alt="view" style={{
            position: 'absolute', bottom: '6px', right: '6px',
            width: '16px', height: '16px', opacity: 0.8,
          }} />
        )}
      </div>
    );
  };

  const canSubmit = uploadedFiles.length > 0 && patient.patientId && address.addressId && !loading;

  return (
    <div>

      {/* ── Step 1: Find Patient ── */}
      <div style={card}>
        {stepLabel(1, 'Find Patient')}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
          <input
            type="tel"
            name="telephone"
            placeholder="Enter 10-digit mobile number"
            maxLength="10"
            value={phone}
            onChange={handlePhoneChange}
            onKeyDown={(e) => { if (e.key === 'Enter') searchUserDataByPhone(); }}
            style={{
              flex: 1, height: '40px', borderRadius: '7px',
              border: `1.5px solid ${BORDER}`, padding: '0 12px',
              fontSize: '13px', outline: 'none', color: TEXT,
              transition: 'border-color 0.15s',
            }}
            onFocus={(e) => e.target.style.borderColor = BRAND}
            onBlur={(e) => e.target.style.borderColor = BORDER}
          />
          <button
            onClick={searchUserDataByPhone}
            disabled={searchLoading || !phone}
            style={{
              height: '40px', padding: '0 20px',
              background: searchLoading || !phone ? '#e2e8f0' : BRAND,
              color: searchLoading || !phone ? '#94a3b8' : '#fff',
              border: 'none', borderRadius: '7px',
              fontSize: '13px', fontWeight: '600', cursor: searchLoading || !phone ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '7px',
              transition: 'background 0.15s',
              flexShrink: 0,
            }}
          >
            <FaSearch style={{ fontSize: '12px' }} />
            {searchLoading ? 'Searching…' : 'Search'}
          </button>
        </div>

        {searchError && (
          <div style={{
            marginTop: '10px', padding: '9px 14px',
            background: '#fef2f2', border: '1px solid #fecaca',
            borderLeft: '3px solid #ef4444', borderRadius: '6px',
            fontSize: '12px', color: '#b91c1c', fontWeight: '500',
          }}>
            {searchError}
          </div>
        )}

        {user.name && (
          <div style={{
            marginTop: '12px', padding: '12px 16px',
            background: '#f5f3ff', border: `1px solid ${BORDER}`,
            borderLeft: `3px solid ${BRAND}`, borderRadius: '8px',
            display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center',
          }}>
            <FaCheckCircle style={{ color: '#16a34a', fontSize: '16px', flexShrink: 0 }} />
            {[
              { icon: <FaUser />, label: 'Name', value: user.name },
              { icon: <FaBuilding />, label: 'Sponsor', value: sponsor?.name || 'B2C' },
              { icon: <FaPhone />, label: 'Phone', value: user.phone },
            ].map(({ icon, label, value }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: BRAND, fontSize: '11px' }}>{icon}</span>
                <span style={{ fontSize: '12px', color: MUTED, fontWeight: '500' }}>{label}:</span>
                <span style={{ fontSize: '12px', color: TEXT, fontWeight: '700' }}>{value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Step 2: Patient & Address ── */}
      {user.name && (
        <div style={card}>
          {stepLabel(2, 'Select Patient & Delivery Address')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>
                Patient
              </label>
              <Select
                options={patients}
                placeholder="Select patient…"
                onChange={(e) => setPatient(e)}
                styles={selectStyles}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>
                Delivery Address
              </label>
              <Select
                options={addresses}
                placeholder="Select address…"
                onChange={(e) => setAddress(e)}
                formatOptionLabel={formatAddressOptionLabel}
                styles={selectStyles}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Upload Files ── */}
      {user.name && (
        <div style={card}>
          {stepLabel(3, 'Upload Prescription Files')}

          {/* Hidden file inputs */}
          <input type="file" id="photoAttachment" accept="image/*" style={{ display: 'none' }}
            onChange={() => handleAttachment('photo')} />
          <input type="file" id="fileAttachment" accept=".pdf" style={{ display: 'none' }}
            onChange={() => handleAttachment('file')} />
          <input type="file" id="captureAttachment" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={() => handleAttachment('photo')} />

          {/* Upload progress */}
          {uploading && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: MUTED, fontWeight: '500' }}>Uploading file…</span>
                <span style={{ fontSize: '12px', color: BRAND, fontWeight: '700' }}>{Math.round(uploadProgress)}%</span>
              </div>
              <div style={{ height: '6px', background: '#e0daf8', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', background: BRAND, borderRadius: '3px',
                  width: `${uploadProgress}%`, transition: 'width 0.2s ease',
                }} />
              </div>
            </div>
          )}

          {/* Upload zone */}
          {uploadedFiles.length === 0 && !uploading ? (
            <div style={{
              border: `2px dashed ${BORDER}`, borderRadius: '10px',
              padding: '32px 24px', textAlign: 'center',
              background: '#faf9ff',
            }}>
              <div style={{ fontSize: '12px', color: MUTED, fontWeight: '500', marginBottom: '18px' }}>
                Select a file format to upload the prescription
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                {[
                  { id: 'photoAttachment', Icon: FaImage,   label: 'Image', color: '#0ea5e9', bg: '#f0f9ff', border: '#bae6fd' },
                  { id: 'fileAttachment',  Icon: FaFilePdf, label: 'PDF',   color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
                ].map(({ id, Icon, label, color, bg, border }) => (
                  <button
                    key={id}
                    onClick={() => document.getElementById(id).click()}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      gap: '8px', padding: '18px 28px',
                      background: bg, border: `1.5px solid ${border}`, borderRadius: '10px',
                      cursor: 'pointer', transition: 'box-shadow 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 4px 12px ${border}`}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <Icon style={{ fontSize: '28px', color }} />
                    <span style={{ fontSize: '12px', fontWeight: '700', color }}>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* File preview grid */}
              {uploadedFiles.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '10px' }}>
                    Uploaded files ({uploadedFiles.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', alignItems: 'flex-start' }}>
                    {uploadedFiles.map(renderUploadedFile)}
                    {/* Add more: Image */}
                    <button
                      onClick={() => document.getElementById('photoAttachment').click()}
                      style={{
                        width: '80px', height: '96px', borderRadius: '10px',
                        border: `2px dashed #bae6fd`, background: '#f0f9ff',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: '4px', cursor: 'pointer', flexShrink: 0,
                      }}
                    >
                      <FaImage style={{ fontSize: '20px', color: '#0ea5e9' }} />
                      <span style={{ fontSize: '10px', fontWeight: '600', color: '#0ea5e9' }}>+ Image</span>
                    </button>
                    {/* Add more: PDF */}
                    <button
                      onClick={() => document.getElementById('fileAttachment').click()}
                      style={{
                        width: '80px', height: '96px', borderRadius: '10px',
                        border: `2px dashed #fecaca`, background: '#fef2f2',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: '4px', cursor: 'pointer', flexShrink: 0,
                      }}
                    >
                      <FaFilePdf style={{ fontSize: '20px', color: '#ef4444' }} />
                      <span style={{ fontSize: '10px', fontWeight: '600', color: '#ef4444' }}>+ PDF</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Submit ── */}
      {user.name && (
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            width: '100%', height: '44px', borderRadius: '8px', border: 'none',
            background: canSubmit ? `linear-gradient(135deg, ${BRAND} 0%, #8b7cf6 100%)` : '#e2e8f0',
            color: canSubmit ? '#fff' : '#94a3b8',
            fontSize: '14px', fontWeight: '700', cursor: canSubmit ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            boxShadow: canSubmit ? '0 4px 12px rgba(108,93,211,0.3)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {loading ? <LoadingComponent /> : (
            <>
              <FaCheckCircle style={{ fontSize: '14px' }} />
              Submit Prescription
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default UploadFile;
